import { createContext, useEffect, useState, useMemo } from "react";

export const DarkModeContext = createContext();

const getStoredMode = () => {
  if (typeof window === 'undefined') return 'system';
  const stored = window.localStorage.getItem('themeMode');
  return ['light', 'dark', 'system'].includes(stored) ? stored : 'system';
};

const getSystemPrefersDark = () => {
  if (typeof window === 'undefined' || !window.matchMedia) return false;
  return window.matchMedia('(prefers-color-scheme: dark)').matches;
};

export const DarkModeProvider = ({ children }) => {
  const [mode, setMode] = useState(() => getStoredMode());
  const [systemPrefersDark, setSystemPrefersDark] = useState(() => getSystemPrefersDark());

  const darkMode = useMemo(() => {
    if (mode === 'dark') return true;
    if (mode === 'light') return false;
    return systemPrefersDark;
  }, [mode, systemPrefersDark]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    window.localStorage.setItem('themeMode', mode);
  }, [mode]);

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return undefined;
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const listener = (event) => setSystemPrefersDark(event.matches);
    mediaQuery.addEventListener ? mediaQuery.addEventListener('change', listener) : mediaQuery.addListener(listener);
    // Sync immediately in case system preference changed since first render
    setSystemPrefersDark(mediaQuery.matches);
    return () => {
      mediaQuery.removeEventListener ? mediaQuery.removeEventListener('change', listener) : mediaQuery.removeListener(listener);
    };
  }, []);

  const setThemeMode = (nextMode) => {
    const normalized = ['light', 'dark', 'system'].includes(nextMode) ? nextMode : 'system';
    setMode(normalized);
  };

  const toggleDarkMode = () => {
    if (mode === 'system') {
      setMode(darkMode ? 'light' : 'dark');
    } else {
      setMode(mode === 'dark' ? 'light' : 'dark');
    }
  };

  return (
    <DarkModeContext.Provider value={{ darkMode, mode, setThemeMode, toggleDarkMode }}>
      {children}
    </DarkModeContext.Provider>
  );
};
